import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { PublicUnitCard, type AvailableUnitGroup } from '@/components/public/units';
import { formatPropertyAddress } from '@/lib/utils/propertyFormatting';
import type { AvailabilitiesListContent } from '@/lib/types/siteSettings';
import { applyTemplate } from '@/lib/utils/textTemplates';
import { formatAssetTypeLabel } from '@/lib/constants/propertyOptions';

export function AvailabilityGroup({
  group,
  content,
}: {
  group: AvailableUnitGroup;
  content: AvailabilitiesListContent;
}): JSX.Element {
  const addressLabel = formatPropertyAddress(group.property.address);
  const unitCountLabel =
    group.units.length === 1
      ? content.unitCountSingular
      : applyTemplate(content.unitCountPlural, { count: group.units.length.toString() });
  const assetLabel = formatAssetTypeLabel(group.property.assetType);
  const submarketLabel = group.property.submarket;
  const propertyHref = `/properties/${group.property.propertyCode.toLowerCase()}`;

  return (
    <section className="flex flex-col gap-4 rounded-3xl border border-surface-subtle bg-white/70 p-5 shadow-card md:p-6">
      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div className="flex flex-col gap-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-brand-accent">
            {content.addressEyebrow}
          </p>
          <h2 className="text-2xl font-semibold text-brand-primary">{addressLabel}</h2>
          <p className="text-sm text-text-primary/70">
            {submarketLabel}
            {assetLabel ? (
              <span data-ui="Public.PropertyCard.AssetType">{` - ${assetLabel}`}</span>
            ) : null}
          </p>
          <p className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
            {unitCountLabel}
          </p>
        </div>

        <Link
          to={propertyHref}
          className="inline-flex items-center justify-center rounded-full border border-brand-primary/30 px-4 py-2 text-sm font-semibold text-brand-primary transition hover:border-brand-primary hover:bg-brand-primary/10"
        >
          {content.viewPropertyLabel}
        </Link>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {group.units.map((unit) => (
          <PublicUnitCard
            key={unit.id}
            property={group.property}
            unit={unit}
            href={`/properties/${group.property.propertyCode.toLowerCase()}/units/${unit.id}`}
          />
        ))}
      </div>
    </section>
  );
}
